import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Button, Spinner, Badge } from "../../components/ui";
import { STATUS_META, SEVERITIES, pct, type CheckStatus, type CheckView, type Counts, type InspectionView, type Severity } from "../../lib/inspection";
import { VehicleDiagram } from "../../components/VehicleDiagram";
import { ZONE_CODES } from "../../lib/vehicle";

function compositeVal(value: Record<string, unknown> | null, key: string): string {
  const fields = value?.fields as Record<string, unknown> | undefined;
  const v = fields?.[key];
  return typeof v === "string" || typeof v === "number" ? String(v) : "";
}

function tally(checks: { status: CheckStatus }[]): Counts {
  const c: Counts = { total: checks.length, checked: 0, ok: 0, observation: 0, defect: 0, notApplicable: 0, notChecked: 0 };
  for (const ck of checks) {
    if (ck.status === "OK") { c.ok++; c.checked++; }
    else if (ck.status === "OBSERVATION") { c.observation++; c.checked++; }
    else if (ck.status === "DEFECT") { c.defect++; c.checked++; }
    else if (ck.status === "NOT_APPLICABLE") { c.notApplicable++; c.checked++; }
    else c.notChecked++;
  }
  return c;
}

export function CrmInspect() {
  const { id: caseId = "" } = useParams();
  const [view, setView] = useState<InspectionView | null>(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [sectionIdx, setSectionIdx] = useState(0);
  const [saving, setSaving] = useState(false);
  const [review, setReview] = useState(false);
  const [showMap, setShowMap] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setView(await api.get<InspectionView>(`/api/crm/cases/${caseId}/inspection`));
    } catch {
      setView(null);
    } finally {
      setLoading(false);
    }
  }, [caseId]);
  useEffect(() => { void load(); }, [load]);

  const iid = view?.inspection.id;
  const signed = view?.inspection.status !== "IN_PROGRESS";

  // Optimistic local update of a check, background-persisted.
  const patchCheck = useCallback(async (checkId: string, patch: Partial<CheckView>, body: Record<string, unknown>) => {
    setView((v) => v && { ...v, sections: v.sections.map((s) => ({ ...s, checks: s.checks.map((c) => (c.id === checkId ? { ...c, ...patch } : c)) })) });
    setSaving(true);
    try { await api.patch(`/api/crm/inspections/${iid}/checks/${checkId}`, body); } finally { setSaving(false); }
  }, [iid]);

  if (loading) return <div className="flex min-h-dvh items-center justify-center"><Spinner /></div>;

  if (!view) {
    return (
      <div className="mx-auto max-w-md py-20 text-center">
        <h1 className="text-xl font-semibold">Inspección del vehículo</h1>
        <p className="mt-2 text-sm text-muted">Aún no se ha iniciado la inspección de este expediente.</p>
        <div className="mt-6 flex justify-center gap-3">
          <Link to={`/cases/${caseId}`} className="btn btn-ghost">Volver</Link>
          <Button disabled={starting} onClick={() => { setStarting(true); void api.post(`/api/crm/cases/${caseId}/inspection`).then(load).finally(() => setStarting(false)); }}>
            {starting ? "Iniciando…" : "Iniciar inspección"}
          </Button>
        </div>
      </div>
    );
  }

  const section = view.sections[sectionIdx];
  const overall = tally(view.sections.flatMap((s) => s.checks));

  return (
    <div className="min-h-dvh bg-canvas">
      {/* Top bar */}
      <div className="sticky top-0 z-10 border-b border-line bg-surface">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-2.5">
          <Link to={`/cases/${caseId}`} className="text-sm text-muted hover:underline">← Expediente</Link>
          <div className="flex items-center gap-3 text-sm">
            <span className="text-muted">{saving ? "Guardando…" : "Guardado ✓"}</span>
            <span className="tnum font-semibold">{pct(overall)}%</span>
            {!signed && <Button variant="ghost" onClick={() => setShowMap(true)}>🚗 Daños</Button>}
            <Button onClick={() => setReview(true)}>Revisar y firmar</Button>
          </div>
        </div>
        <div className="h-1 w-full bg-line"><div className="h-1 bg-accent transition-all" style={{ width: `${pct(overall)}%` }} /></div>
        {/* Section rail */}
        <div className="mx-auto flex max-w-5xl gap-2 overflow-x-auto px-4 py-2">
          {view.sections.map((s, i) => {
            const c = tally(s.checks);
            const tone = c.defect > 0 ? "danger" : c.checked === c.total ? "ok" : "neutral";
            return (
              <button key={s.id} onClick={() => setSectionIdx(i)}
                className={`shrink-0 rounded-lg border px-3 py-1.5 text-xs font-medium transition ${i === sectionIdx ? "border-accent bg-accent-050 text-accent-600" : "border-line bg-surface text-muted hover:bg-canvas"}`}>
                {s.title} <span className={`ml-1 ${tone === "danger" ? "text-danger" : tone === "ok" ? "text-ok" : ""}`}>{c.checked}/{c.total}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Section body */}
      <div className="mx-auto max-w-3xl px-4 py-6">
        {section && (
          <>
            <h2 className="mb-4 text-lg font-semibold">{section.title}</h2>
            <div className="flex flex-col gap-3">
              {section.checks.map((check) => (
                <CheckCard key={check.id} check={check} disabled={signed} iid={iid!}
                  findingForCheck={view.findings.find((f) => f.checkId === check.id) ?? null}
                  onPatch={patchCheck} onReload={load} />
              ))}
            </div>
            <div className="mt-6 flex justify-between">
              <Button variant="ghost" disabled={sectionIdx === 0} onClick={() => setSectionIdx((i) => Math.max(0, i - 1))}>← Anterior</Button>
              {sectionIdx < view.sections.length - 1
                ? <Button onClick={() => setSectionIdx((i) => i + 1)}>Siguiente →</Button>
                : <Button onClick={() => setReview(true)}>Revisar y firmar</Button>}
            </div>
          </>
        )}
      </div>

      {review && iid && <ReviewSignoff caseId={caseId} iid={iid} view={view} onClose={() => setReview(false)} onReload={load} />}
      {showMap && iid && <DamageMap iid={iid} view={view} onClose={() => setShowMap(false)} onReload={load} />}
    </div>
  );
}

const BIG: { status: CheckStatus; cls: string }[] = [
  { status: "OK", cls: "bg-ok-050 text-ok border-ok/30" },
  { status: "OBSERVATION", cls: "bg-warn-050 text-warn border-warn/30" },
  { status: "DEFECT", cls: "bg-danger-050 text-danger border-danger/30" },
  { status: "NOT_APPLICABLE", cls: "bg-canvas text-muted border-line" },
];

function CheckCard({ check, disabled, iid, findingForCheck, onPatch, onReload }: {
  check: CheckView; disabled: boolean; iid: string;
  findingForCheck: { id: string } | null;
  onPatch: (checkId: string, patch: Partial<CheckView>, body: Record<string, unknown>) => Promise<void>;
  onReload: () => Promise<void>;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const expanded = check.status === "OBSERVATION" || check.status === "DEFECT";

  async function setStatus(status: CheckStatus) {
    await onPatch(check.id, { status }, { status });
    // A defect becomes a first-class finding (once) so it appears in the defects list + report.
    if (status === "DEFECT" && !findingForCheck) {
      await api.post(`/api/crm/inspections/${iid}/findings`, { checkId: check.id, vehicleArea: check.key, title: check.label, severity: check.severity ?? "MAJOR" });
      await onReload();
    }
  }
  async function setValue(value: Record<string, unknown>) {
    await onPatch(check.id, { value }, { value, ...(check.status === "NOT_CHECKED" ? { status: "OK" } : {}) });
  }
  async function uploadPhoto(file: File) {
    setUploading(true);
    try { await api.upload(`/api/crm/inspections/${iid}/checks/${check.id}/media`, file); await onReload(); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  }

  return (
    <div className="card p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-medium">{check.label} {check.required && <span className="text-danger">*</span>}</div>
          {check.instructions && <div className="mt-0.5 text-xs text-muted">{check.instructions}</div>}
        </div>
        {check.media.length > 0 && <Badge tone="info">{check.media.length} 📷</Badge>}
      </div>

      {/* Type-specific input */}
      {check.checkType === "NUMERIC" && (
        <div className="mt-3 flex items-center gap-2">
          <input type="number" disabled={disabled} defaultValue={typeof check.value?.number === "number" ? check.value.number : ""} onBlur={(e) => e.target.value !== "" && void setValue({ number: Number(e.target.value) })} className="input max-w-[10rem]" placeholder="0" />
          {check.unit && <span className="text-sm text-muted">{check.unit}</span>}
        </div>
      )}
      {check.checkType === "TEXT" && (
        <input disabled={disabled} defaultValue={typeof check.value?.text === "string" ? check.value.text : ""} onBlur={(e) => e.target.value && void setValue({ text: e.target.value })} className="input mt-3" placeholder="…" />
      )}
      {check.checkType === "OPTION" && check.config?.options && (
        <div className="mt-3 flex flex-wrap gap-2">
          {check.config.options.map((o) => (
            <button key={o} disabled={disabled} onClick={() => void setValue({ option: o })}
              className={`rounded-lg border px-3 py-1.5 text-sm ${check.value?.option === o ? "border-accent bg-accent-050 text-accent-600" : "border-line"}`}>{o}</button>
          ))}
        </div>
      )}
      {check.checkType === "COMPOSITE" && check.config?.fields && (
        <div className="mt-3 grid grid-cols-2 gap-2">
          {check.config.fields.map((f) => (
            <label key={f.key} className="text-xs text-muted">{f.label}{f.unit ? ` (${f.unit})` : ""}
              <input disabled={disabled} className="input mt-1" defaultValue={compositeVal(check.value, f.key)}
                onBlur={(e) => { const fields = { ...(check.value?.fields as Record<string, unknown> ?? {}), [f.key]: e.target.value }; if (e.target.value) void setValue({ fields }); }} />
            </label>
          ))}
        </div>
      )}

      {/* Big status buttons */}
      <div className="mt-3 grid grid-cols-4 gap-2">
        {BIG.map((b) => (
          <button key={b.status} disabled={disabled} onClick={() => void setStatus(b.status)}
            className={`rounded-xl border px-2 py-3 text-sm font-semibold transition ${check.status === b.status ? b.cls + " ring-2 ring-offset-1" : "border-line bg-surface text-muted hover:bg-canvas"}`}>
            {STATUS_META[b.status].icon} {STATUS_META[b.status].label}
          </button>
        ))}
      </div>

      {/* Defect / observation detail */}
      {expanded && (
        <div className="mt-3 rounded-lg bg-canvas p-3">
          <div className="flex flex-wrap gap-2">
            {SEVERITIES.map((sev) => (
              <button key={sev} disabled={disabled} onClick={() => void onPatch(check.id, { severity: sev }, { severity: sev })}
                className={`rounded-lg border px-3 py-1 text-xs font-medium ${check.severity === sev ? "border-accent bg-accent-050 text-accent-600" : "border-line"}`}>{sev}</button>
            ))}
          </div>
          <textarea disabled={disabled} defaultValue={check.comment ?? ""} onBlur={(e) => void onPatch(check.id, { comment: e.target.value }, { comment: e.target.value })}
            className="input mt-2" rows={2} placeholder="Descripción del defecto/observación…" />
        </div>
      )}

      {/* Photos */}
      {(check.checkType === "PHOTO" || expanded || check.photoRequired) && !disabled && (
        <div className="mt-3">
          <input ref={fileRef} type="file" accept="image/*" capture="environment" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) void uploadPhoto(f); }} />
          <Button variant="ghost" disabled={uploading} onClick={() => fileRef.current?.click()}>{uploading ? "Subiendo…" : "📷 Añadir foto"}</Button>
          {check.photoRequired && check.media.length === 0 && check.status !== "NOT_APPLICABLE" && <span className="ml-2 text-xs text-warn">Foto obligatoria</span>}
        </div>
      )}
    </div>
  );
}

function ReviewSignoff({ caseId, iid, view, onClose, onReload }: { caseId: string; iid: string; view: InspectionView; onClose: () => void; onReload: () => Promise<void> }) {
  const { principal } = useAuth();
  const navigate = useNavigate();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [rec, setRec] = useState(view.inspection.overallRecommendation ?? "");
  const [conclusion, setConclusion] = useState(view.inspection.conclusion ?? "");
  const [blockers, setBlockers] = useState<{ reason: string }[]>([]);
  const [override, setOverride] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const overall = tally(view.sections.flatMap((s) => s.checks));
  const isSupervisor = principal?.role === "SUPER_ADMIN" || principal?.role === "OPERATIONS";
  const signed = view.inspection.status !== "IN_PROGRESS";

  useEffect(() => {
    void api.get<{ blockers: { reason: string }[] }>(`/api/crm/inspections/${iid}/completion`).then((r) => setBlockers(r.blockers)).catch(() => setBlockers([]));
  }, [iid]);

  // Signature pad
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.strokeStyle = "#13223a"; ctx.lineWidth = 2; ctx.lineCap = "round";
    let drawing = false;
    const pos = (e: PointerEvent) => { const r = canvas.getBoundingClientRect(); return { x: e.clientX - r.left, y: e.clientY - r.top }; };
    const down = (e: PointerEvent) => { drawing = true; const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
    const move = (e: PointerEvent) => { if (!drawing) return; const p = pos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); };
    const up = () => { drawing = false; };
    canvas.addEventListener("pointerdown", down); canvas.addEventListener("pointermove", move); window.addEventListener("pointerup", up);
    return () => { canvas.removeEventListener("pointerdown", down); canvas.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
  }, []);

  function clearSig() { const c = canvasRef.current; const ctx = c?.getContext("2d"); if (c && ctx) ctx.clearRect(0, 0, c.width, c.height); }

  async function finalize() {
    setBusy(true); setError(null);
    try {
      if (rec) await api.patch(`/api/crm/inspections/${iid}`, { overallRecommendation: rec, conclusion });
      const sig = canvasRef.current?.toDataURL("image/png");
      await api.post(`/api/crm/inspections/${iid}/finalize`, {
        signerName: principal?.userId ? "Inspector" : "Inspector",
        signatureImageBase64: sig,
        ...(override && reason ? { overrideReason: reason } : {}),
      });
      await onReload();
      void navigate(`/cases/${caseId}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo finalizar");
      await api.get<{ blockers: { reason: string }[] }>(`/api/crm/inspections/${iid}/completion`).then((r) => setBlockers(r.blockers)).catch(() => undefined);
    } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-20 overflow-y-auto bg-black/40 p-4" onClick={onClose}>
      <div className="mx-auto max-w-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="card p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Revisión y firma</h2>
            <button onClick={onClose} className="text-muted">✕</button>
          </div>

          <div className="mt-4 grid grid-cols-3 gap-3 text-center sm:grid-cols-6">
            {[["Puntos", overall.total], ["OK", overall.ok], ["Obs.", overall.observation], ["Defectos", overall.defect], ["N/A", overall.notApplicable], ["Sin compr.", overall.notChecked]].map(([l, n]) => (
              <div key={l} className="rounded-lg bg-canvas p-2"><div className="text-lg font-semibold tnum">{n}</div><div className="text-xs text-muted">{l}</div></div>
            ))}
          </div>

          {view.findings.length > 0 && (
            <div className="mt-4">
              <div className="mb-1 text-sm font-semibold">{view.findings.length} incidencia(s)</div>
              <ul className="divide-y divide-line rounded-lg border border-line">
                {view.findings.map((f) => (
                  <li key={f.id} className="flex items-center justify-between px-3 py-2 text-sm"><span>{f.vehicleArea ?? ""} — {f.title}</span><Badge tone={f.severity === "CRITICAL" || f.severity === "MAJOR" ? "danger" : "warn"}>{f.severity}</Badge></li>
                ))}
              </ul>
            </div>
          )}

          {blockers.length > 0 && (
            <div className="mt-4 rounded-lg bg-warn-050 p-3 text-sm text-warn">
              <div className="font-semibold">No puedes finalizar todavía:</div>
              <ul className="mt-1 list-disc pl-5">{blockers.slice(0, 8).map((b, i) => <li key={i}>{b.reason}</li>)}</ul>
              {blockers.length > 8 && <div className="mt-1">…y {blockers.length - 8} más.</div>}
            </div>
          )}

          <div className="mt-4">
            <span className="label">Resultado</span>
            <div className="flex flex-wrap gap-2">
              {([["RECOMMENDED", "✅ Recomendado"], ["RECOMMENDED_WITH_OBSERVATIONS", "⚠️ Con observaciones"], ["NOT_RECOMMENDED", "❌ No recomendado"]] as [string, string][]).map(([val, lbl]) => (
                <button key={val} disabled={signed} onClick={() => setRec(val)} className={`rounded-lg border px-3 py-2 text-sm ${rec === val ? "border-accent bg-accent-050 text-accent-600" : "border-line"}`}>{lbl}</button>
              ))}
            </div>
          </div>
          <textarea disabled={signed} value={conclusion} onChange={(e) => setConclusion(e.target.value)} className="input mt-3" rows={3} placeholder="Conclusión profesional…" />

          <div className="mt-4">
            <span className="label">Firma del inspector</span>
            <canvas ref={canvasRef} width={520} height={140} className="w-full touch-none rounded-lg border border-line bg-white" />
            <button onClick={clearSig} className="mt-1 text-xs text-muted hover:underline">Borrar firma</button>
          </div>

          {isSupervisor && blockers.length > 0 && (
            <label className="mt-3 flex items-start gap-2 text-sm">
              <input type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)} className="mt-1" />
              <span>Finalizar como supervisor pese a lo pendiente. <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Motivo" className="input mt-1" /></span>
            </label>
          )}

          {error && <p className="mt-3 rounded-lg bg-danger-050 px-3 py-2 text-sm text-danger">{error}</p>}

          <div className="mt-5 flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose}>Seguir editando</Button>
            <Button disabled={busy || !rec || (blockers.length > 0 && !(override && reason))} onClick={() => void finalize()}>{busy ? "Finalizando…" : "Finalizar inspección"}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function DamageMap({ iid, view, onClose, onReload }: { iid: string; view: InspectionView; onClose: () => void; onReload: () => Promise<void> }) {
  const bodyFindings = view.findings.filter((f) => f.vehicleArea && ZONE_CODES.has(f.vehicleArea));
  const [zone, setZone] = useState<{ code: string; label: string } | null>(null);
  const [severity, setSeverity] = useState<Severity>("MAJOR");
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  async function register() {
    if (!zone) return;
    setBusy(true);
    try {
      const res = await api.post<{ id: string }>(`/api/crm/inspections/${iid}/findings`, { vehicleArea: zone.code, title: zone.label, severity, description: desc || undefined });
      if (file) await api.upload(`/api/crm/inspections/${iid}/findings/${res.id}/media`, file);
      setZone(null); setDesc(""); setSeverity("MAJOR"); setFile(null); if (fileRef.current) fileRef.current.value = "";
      await onReload();
    } finally { setBusy(false); }
  }
  async function remove(id: string) { await api.del(`/api/crm/inspections/${iid}/findings/${id}`); await onReload(); }

  return (
    <div className="fixed inset-0 z-20 overflow-y-auto bg-black/40 p-4" onClick={onClose}>
      <div className="mx-auto max-w-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="card p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Daños de carrocería</h2>
            <button onClick={onClose} className="text-muted">✕</button>
          </div>
          <p className="mt-1 text-sm text-muted">Toca una zona del vehículo para registrar un daño.</p>

          <div className="mt-4 grid gap-6 sm:grid-cols-2">
            <VehicleDiagram findings={bodyFindings} onZoneClick={(code, label) => setZone({ code, label })} selected={zone?.code} />

            <div>
              {zone ? (
                <div className="rounded-xl border border-line p-4">
                  <div className="text-sm font-semibold">{zone.label}</div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {SEVERITIES.map((sev) => (
                      <button key={sev} onClick={() => setSeverity(sev)} className={`rounded-lg border px-3 py-1.5 text-sm ${severity === sev ? "border-accent bg-accent-050 text-accent-600" : "border-line"}`}>{sev}</button>
                    ))}
                  </div>
                  <textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={2} className="input mt-3" placeholder="Descripción (golpe, arañazo, corrosión…)" />
                  <input ref={fileRef} type="file" accept="image/*" capture="environment" hidden onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
                  <div className="mt-2 flex items-center gap-2">
                    <Button variant="ghost" onClick={() => fileRef.current?.click()}>{file ? "📷 1 foto" : "📷 Foto"}</Button>
                    <Button disabled={busy} onClick={() => void register()}>{busy ? "Guardando…" : "Registrar daño"}</Button>
                    <Button variant="ghost" onClick={() => setZone(null)}>Cancelar</Button>
                  </div>
                </div>
              ) : (
                <div className="rounded-xl border border-dashed border-line p-4 text-sm text-muted">Selecciona una zona en el diagrama.</div>
              )}

              <div className="mt-4">
                <div className="mb-1 text-sm font-semibold">{bodyFindings.length} daño(s) de carrocería</div>
                <ul className="divide-y divide-line rounded-lg border border-line">
                  {bodyFindings.length === 0 && <li className="px-3 py-2 text-sm text-muted">Sin daños registrados.</li>}
                  {bodyFindings.map((f) => (
                    <li key={f.id} className="flex items-center justify-between px-3 py-2 text-sm">
                      <span>{f.title}</span>
                      <span className="flex items-center gap-2">
                        <Badge tone={f.severity === "CRITICAL" || f.severity === "MAJOR" ? "danger" : "warn"}>{f.severity}</Badge>
                        <button onClick={() => void remove(f.id)} className="text-xs text-muted hover:text-danger hover:underline">Quitar</button>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <div className="mt-5 flex justify-end"><Button onClick={onClose}>Hecho</Button></div>
        </div>
      </div>
    </div>
  );
}
